import { useState } from 'react';
import { MoreVertical, PencilLine, Plus, Sparkles, Trash2, Wallet } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { Menu } from '@/components/ui/Menu';
import { QueryError } from '@/components/ui/QueryError';
import { Table, type Column } from '@/components/ui/Table';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import type { SalaryComponent } from '@/types/payroll';
import { payrollApi } from '../api/payroll.api';
import { PayHeadFormModal } from '../components/PayHeadFormModal';
import { SALARY_CALCULATION_LABEL, SALARY_KIND_LABEL } from '../constants';
import { describeValue } from '../format';

/**
 * The institute's pay heads — what every salary is built from.
 *
 * Phase one of payroll: the heads here, and each person's salary on their
 * profile. The monthly run that turns salaries into payslips comes next, and
 * will take `/admin/payroll` itself.
 */
export function PayHeadsPage() {
  const permissions = useAuthStore((state) => state.user?.permissions);
  const canManage = Boolean(permissions?.includes('PAYROLL_MANAGE'));

  const { data, isLoading, error, refetch } = useApiQuery(() => payrollApi.listComponents(), []);
  const [editing, setEditing] = useState<SalaryComponent | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [removing, setRemoving] = useState<SalaryComponent | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [isAddingDefaults, setIsAddingDefaults] = useState(false);

  function openForm(head: SalaryComponent | null) {
    setEditing(head);
    setIsFormOpen(true);
  }

  async function addDefaults() {
    setIsAddingDefaults(true);
    try {
      const heads = await payrollApi.addDefaultComponents();
      toast.success('Common pay heads added', `${heads.length} pay heads are ready. Adjust any default to suit your school.`);
      refetch();
    } catch (caught) {
      toast.error(
        'Could not add the pay heads',
        caught instanceof ApiRequestError ? caught.message : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsAddingDefaults(false);
    }
  }

  async function confirmRemove() {
    if (!removing) return;
    setIsRemoving(true);
    try {
      await payrollApi.removeComponent(removing.uuid);
      toast.success(`${removing.name} removed`);
      setRemoving(null);
      refetch();
    } catch (caught) {
      toast.error(
        'Could not remove this pay head',
        caught instanceof ApiRequestError ? caught.message : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsRemoving(false);
    }
  }

  const columns: Column<SalaryComponent>[] = [
    {
      key: 'name',
      header: 'Pay head',
      render: (head) => (
        <div className="min-w-0">
          <p className="truncate font-[560] text-ink">{head.name}</p>
          <p className="font-mono text-xs text-ink-3">{head.code}</p>
        </div>
      ),
    },
    {
      key: 'kind',
      header: 'Type',
      render: (head) => (
        <Badge tone={head.kind === 'EARNING' ? 'success' : 'critical'}>{SALARY_KIND_LABEL[head.kind]}</Badge>
      ),
    },
    {
      key: 'calculation',
      header: 'Worked out as',
      hideOnMobile: true,
      render: (head) => <span className="text-ink-2">{SALARY_CALCULATION_LABEL[head.calculation]}</span>,
    },
    {
      key: 'default',
      header: 'Default',
      render: (head) => <span className="text-ink">{describeValue(head.calculation, head.defaultValue)}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      hideOnMobile: true,
      render: (head) => (
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge tone={head.isActive ? 'accent' : 'neutral'}>{head.isActive ? 'In use' : 'Switched off'}</Badge>
          {head.isStatutory && <Badge tone="info">Statutory</Badge>}
        </div>
      ),
    },
    {
      key: 'inUse',
      header: 'On salaries',
      align: 'right',
      hideOnMobile: true,
      render: (head) => <span className="tabular-nums text-ink-2">{head.inUse}</span>,
    },
    ...(canManage
      ? [
          {
            key: 'actions',
            header: <span className="sr-only">Actions</span>,
            align: 'right' as const,
            render: (head: SalaryComponent) => (
              <Menu
                label={`Actions for ${head.name}`}
                trigger={<MoreVertical className="h-4 w-4" aria-hidden />}
                items={[
                  {
                    key: 'edit',
                    label: 'Edit',
                    icon: <PencilLine className="h-4 w-4" />,
                    onSelect: () => openForm(head),
                  },
                  // A head on somebody's salary is switched off, never removed —
                  // so the menu only offers removal where the server would allow it.
                  ...(head.inUse === 0
                    ? [
                        {
                          key: 'remove',
                          label: 'Remove',
                          icon: <Trash2 className="h-4 w-4" />,
                          tone: 'danger' as const,
                          onSelect: () => setRemoving(head),
                        },
                      ]
                    : []),
                ]}
              />
            ),
          },
        ]
      : []),
  ];

  const addButton = canManage ? (
    <Button className='text-sm font-bold' leftIcon={<Plus className="h-4 w-4" />} onClick={() => openForm(null)}>
      Add pay head
    </Button>
  ) : undefined;

  return (
    <>
      <PageHeader
        eyebrow="Payroll"
        title="Pay heads"
        description="The earnings and deductions every salary is built from. Set each person's salary from their staff profile."
        actions={addButton}
      />

      {error ? (
        <QueryError title="Could not load the pay heads" error={error} onRetry={refetch} />
      ) : (
        <div className="card overflow-hidden">
          <Table
            variant="plain"
            columns={columns}
            rows={data ?? []}
            rowKey={(head) => head.uuid}
            isLoading={isLoading}
            empty={
              <EmptyState
                icon={<Wallet className="h-5 w-5" />}
                title="No pay heads yet"
                description={
                  canManage
                    ? 'Start with the heads most schools use — HRA, DA, Conveyance, PF, Professional Tax — and adjust them, or add your own.'
                    : 'Your school office has not set up any pay heads yet.'
                }
                action={
                  canManage ? (
                    <div className="flex flex-wrap justify-center gap-2">
                      <Button
                        leftIcon={<Sparkles className="h-4 w-4" />}
                        isLoading={isAddingDefaults}
                        onClick={() => void addDefaults()}
                      >
                        Add common pay heads
                      </Button>
                      <Button variant="secondary" onClick={() => openForm(null)}>
                        Add my own
                      </Button>
                    </div>
                  ) : undefined
                }
              />
            }
          />
        </div>
      )}

      <PayHeadFormModal
        head={editing}
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSaved={(saved) => {
          setIsFormOpen(false);
          toast.success(editing ? `${saved.name} updated` : `${saved.name} added`);
          refetch();
        }}
      />

      <ConfirmDialog
        isOpen={Boolean(removing)}
        tone="danger"
        title={`Remove ${removing?.name ?? 'this pay head'}?`}
        description="It is on nobody's salary, so nothing else changes. You can add it again later."
        confirmLabel="Remove"
        isLoading={isRemoving}
        onConfirm={confirmRemove}
        onCancel={() => setRemoving(null)}
      />
    </>
  );
}
